import {
  ClientResponse,
  Conversation,
  ConversationContext,
  Observability,
  RequestAction,
  TriggerParams,
} from '@stripe-internal/conversations-client';
import {createStore} from '@stripe-internal/state-manager';
import {
  InlineDataEventBus,
  InlineDataService,
} from '@stripe-internal/inline-data';
import {
  ViewMode,
  Visibility,
} from '@stripe-internal/support-conversations-controller';

import {Api} from './lib/api/api';
import {
  Audience,
  Profile,
  SupportEntryPointEntryPoint,
  SupportTriggerPointTriggerPoint,
  Urls,
  Visitor,
} from '../generated/types';
import {ParentMessageBus} from './lib/parentMessageBus/ParentMessageBus';
import {SupportConversationsClient} from './lib/SupportConversationsClient';

export type AppAudience = Audience;
export type AppEntryPoint = SupportEntryPointEntryPoint;
export type AppTriggerPoint = SupportTriggerPointTriggerPoint;

export type AppFeatureFlags = Record<string, boolean>;

export type AppVisitor = Visitor;
export type AppProfile = Omit<Profile, 'featureFlags'> & {
  featureFlags: AppFeatureFlags;
};

export interface AppBusMessage {
  type: string;
  data?: object;
  nonce?: string;
}

export type AppParentMessageBus = ParentMessageBus;

export interface AppConfig {
  locale: string;
  audience: AppAudience;
  entryPoint: AppEntryPoint;
  initialVisibility?: 'open' | 'closed' | 'minimized';
  triggerPoint?: AppTriggerPoint;
  stripeThirdPartyBaseUrl: string;
  parentOrigin?: string;
  revision: string;
  publicSentryDsn?: string;
  urls: Urls;
  primaryColor?: string;
  accentColor?: string;
  theme?: string;
  linkBrandName?: string;
  disableTopicSelector?: boolean;
  requiresSession?: boolean;
  expectedMerchant?: string;
  expectedUser?: string;
  viewMode?: ViewMode;
  sessionRetry?: boolean;
  surfaceLoadId: string;
  token: string;
  closeEndsBotConversation?: boolean;
  resumeVisibilityState?: string;
}

export type AppError = {
  message?: string | undefined;
  errorId?: string | undefined;
};

export type AppCloseHandler = ({
  close,
  conversation,
}: {
  close: (skipOnClose: boolean) => void;
  conversation: Conversation | null;
}) => boolean;

export type AppState = {
  initialized: boolean;
  config: AppConfig;
  profile: AppProfile;
  error?: AppError | null;
  currentConversationId: string | null;
  visibility: Visibility;
  viewMode?: ViewMode;
  triggerParams?: TriggerParams;
  showCloseConfirmation: boolean;
  showRestartConfirmation: boolean;
  onClose?: AppCloseHandler;
};

export type AppStateStore = ReturnType<typeof createStore<AppState>>;

export const DEFAULT_VIEW_MODE = ViewMode.Drawer;

export interface App extends Observability {
  stateStore: AppStateStore;
  api: Api;
  client: SupportConversationsClient;
  inlineDataEventBus: InlineDataEventBus;
  inlineDataService: InlineDataService;
  parentMessageBus: ParentMessageBus;

  get state(): AppState;
  get config(): AppConfig;
  get profile(): AppProfile;
  hasFlag(name: string): boolean;

  requestAction(action: Omit<RequestAction, 'name'>): Promise<ClientResponse>;

  getConversation(conversationId: string): Conversation | undefined;
  startConversation(params?: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  }): Promise<Conversation | null>;
  restartConversation(params?: {
    initialQuery?: string;
  }): Promise<Conversation | null>;
  endConversation(conversation?: Conversation): Promise<void>;
  resumeOrStartConversation(params?: {
    conversationId?: string;
    initialQuery?: string;
    restart?: boolean;
    context?: Partial<ConversationContext>;
  }): Promise<Conversation | null>;

  open(): void;
  close(_skipOnClose?: boolean): void;
  minimize(): void;
  init(): void;
  updateConfig(config: AppConfig): void;
  updateProfile(profile: AppProfile): void;
  updateOnClose(onClose?: AppCloseHandler): void;
  updateParentMessageBus(parentMessageBus?: ParentMessageBus): void;
  destroy(): void;
}
